import type { GepEventsPayload } from "./types";

export async function bootstrapTrackingSession(
  endpoint: string,
  body: { merchant_id: string; api_key: string },
): Promise<string | undefined> {
  try {
    const response = await fetch(`${endpoint}/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
    if (!response.ok) return undefined;
    const data = await response.json();
    return typeof data?.token === "string" ? data.token : undefined;
  } catch {
    return undefined;
  }
}

export interface SendResult {
  ok: boolean;
  status?: number;
}

function beaconUrl(
  endpoint: string,
  auth: {
    signedToken?: string;
    widgetToken?: string;
  },
): string {
  if (auth.signedToken) {
    return `${endpoint}/events?gep_token=${encodeURIComponent(auth.signedToken)}`;
  }
  if (auth.widgetToken) {
    return `${endpoint}/events?token=${encodeURIComponent(auth.widgetToken)}`;
  }
  return `${endpoint}/events`;
}

export async function postEvents(
  endpoint: string,
  auth: { signedToken?: string; widgetToken?: string },
  payload: GepEventsPayload,
  options: { beacon?: boolean } = {},
): Promise<SendResult> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (auth.signedToken) headers["x-gep-tracking-token"] = auth.signedToken;
  else if (auth.widgetToken) headers.token = auth.widgetToken;

  // Prefer fetch(keepalive) even on unload: it carries auth headers and lets us
  // observe the status. Fall back to sendBeacon only if fetch throws.
  try {
    const response = await fetch(`${endpoint}/events`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    });
    return { ok: response.ok, status: response.status };
  } catch {
    if (
      options.beacon &&
      typeof navigator !== "undefined" &&
      navigator.sendBeacon
    ) {
      try {
        const blob = new Blob([JSON.stringify(payload)], {
          type: "application/json",
        });
        // sendBeacon cannot set custom headers, so auth travels in the query
        // string (middleware accepts `gep_token` signed, `token` widget).
        const queued = navigator.sendBeacon(beaconUrl(endpoint, auth), blob);
        return { ok: queued };
      } catch {
        return { ok: false };
      }
    }
    return { ok: false };
  }
}
